import { Component, ElementRef, HostListener, ViewChild, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { LucideAngularModule, AlertTriangle, ArrowUpRight, Mail, X } from 'lucide-angular';

import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/services/auth.service';
import { PlanNoticeService } from '../../core/services/plan-notice.service';

/**
 * "You have reached your plan's limit" — and what to do about it.
 *
 * Drawn once, in the layout, whenever any request is refused because of the
 * school's plan. The message itself comes from the server, already in the
 * reader's language; this adds the numbers, the way to a bigger plan, and a
 * route to see the plan as it stands.
 */
@Component({
  selector: 'app-plan-limit-dialog',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, TranslocoPipe],
  templateUrl: './plan-limit-dialog.component.html'
})
export class PlanLimitDialogComponent {
  readonly notices = inject(PlanNoticeService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  readonly AlertTriangle = AlertTriangle;
  readonly ArrowUpRight = ArrowUpRight;
  readonly Mail = Mail;
  readonly X = X;
  readonly supportEmail = environment.supportEmail;

  @ViewChild('panel') panel?: ElementRef<HTMLElement>;

  /** Focus the popup when it opens, so Escape and screen readers land on it. */
  private readonly focusOnOpen = effect(() => {
    if (this.notices.notice()) {
      setTimeout(() => this.panel?.nativeElement.focus());
    }
  });

  get notice() {
    return this.notices.notice();
  }

  /** Which of the three stories this is: a count reached, old history, or a feature not bought. */
  get kind(): 'limit' | 'history' | 'feature' {
    const code = this.notice?.code ?? '';
    if (code.startsWith('plan.limit.')) {
      return 'limit';
    }
    return code === 'plan.attendance_history' ? 'history' : 'feature';
  }

  /** The platform can change the plan itself; nobody at a school can. */
  get canChangePlan(): boolean {
    return this.auth.can('plan.manage');
  }

  /** The school's own plan card lives on School details. */
  get canSeePlan(): boolean {
    return !this.canChangePlan && this.auth.can('school.update');
  }

  get percent(): number {
    const n = this.notice;
    if (!n?.limit) {
      return 100;
    }
    return Math.min(100, Math.round(((n.used ?? 0) / n.limit) * 100));
  }

  viewPlan(): void {
    this.close();
    const schoolId = this.auth.user()?.school_id;
    if (this.canChangePlan) {
      this.router.navigate(schoolId ? ['/schools', schoolId] : ['/plans']);
    } else {
      this.router.navigate(['/school-admin/school']);
    }
  }

  close(): void {
    this.notices.dismiss();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.notice) {
      this.close();
    }
  }
}
